import { useEffect, useState } from 'react'

import { useAppStore, useAppStorePersisted } from '@/store/useAppStore'

const EMPTY_FEATURES: string[] = []

/**
 * オフライン時にキャッシュされた認証データを復元するフック
 * PWAオフラインモードでオンライン認証をバイパスしてコンテンツを表示する
 * @returns shouldShowOfflineContent - オフラインかつ過去にサインイン済みならtrue
 */
export const useOfflineAuth = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const hasSignedInBefore = useAppStorePersisted((state) => state.hasSignedInBefore)
  const cachedAccessiblePages = useAppStorePersisted((state) => state.cachedAccessiblePages)
  const cachedAccessibleDomains = useAppStorePersisted((state) => state.cachedAccessibleDomains)
  const cachedHasFullAccess = useAppStorePersisted((state) => state.cachedHasFullAccess)
  const cachedIsSafeMode = useAppStorePersisted((state) => state.cachedIsSafeMode)
  // 古いキャッシュには features が無いので空配列にする
  const cachedFeatures = useAppStorePersisted((state) => state.cachedFeatures ?? EMPTY_FEATURES)
  const cachedCurrentUserId = useAppStorePersisted((state) => state.cachedCurrentUserId)
  const setAccessiblePages = useAppStore((state) => state.setAccessiblePages)
  const setAccessibleDomains = useAppStore((state) => state.setAccessibleDomains)
  const setHasFullAccess = useAppStore((state) => state.setHasFullAccess)
  const setIsSafeMode = useAppStore((state) => state.setIsSafeMode)
  const setFeatures = useAppStore((state) => state.setFeatures)
  const setCurrentUserId = useAppStore((state) => state.setCurrentUserId)
  const setAuthSyncStatus = useAppStore((state) => state.setAuthSyncStatus)

  const shouldShowOfflineContent = !isOnline && hasSignedInBefore

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // オフライン時にキャッシュデータをAppStoreに復元
  // navigator.onLineも見る（Zustand hydration前にisOnlineがtrueの場合の対策）
  useEffect(() => {
    if (isOnline && navigator.onLine) return
    setAccessiblePages(cachedAccessiblePages)
    setAccessibleDomains(cachedAccessibleDomains)
    setHasFullAccess(cachedHasFullAccess)
    setIsSafeMode(cachedIsSafeMode)
    setFeatures(cachedFeatures)
    setCurrentUserId(cachedCurrentUserId)
    // キャッシュから復元できた時点で「取得済み」とみなす（ローディング表示のまま固まらないように）
    setAuthSyncStatus('success')
  }, [
    isOnline,
    cachedAccessiblePages,
    cachedAccessibleDomains,
    cachedHasFullAccess,
    cachedIsSafeMode,
    cachedFeatures,
    cachedCurrentUserId,
    setAccessiblePages,
    setAccessibleDomains,
    setHasFullAccess,
    setIsSafeMode,
    setFeatures,
    setCurrentUserId,
    setAuthSyncStatus,
  ])

  return { shouldShowOfflineContent }
}
